import React, { useState, useEffect, useCallback } from "react";
import { Calendar, Sparkles, Loader2, X, Check, Clock, AlertTriangle, RefreshCw } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * WeeklyPlanView — visible 7-day calendar of what DoozyAd will do.
 *
 * Each day shows:
 *  - Predicted theme (from LRU rotation)
 *  - Festivals due that day
 *  - Already-scheduled drafts
 *  - Cron actions (auto-draft, retargeting, trends, digest)
 *
 * Founder can SKIP a day → 8 AM cron bails for that date. Re-arm anytime.
 */
export const WeeklyPlanView = ({ authAxios }) => {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [busyDate, setBusyDate] = useState("");
  const [skippedDates, setSkippedDates] = useState({});
  const [dragOverDate, setDragOverDate] = useState("");
  const [draggingDraft, setDraggingDraft] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const r = await authAxios.get(`${API_URL}/api/admin/doozyad/weekly-plan`, { timeout: 20000 });
      setPlan(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load weekly plan");
    } finally {
      setLoading(false);
    }
  }, [authAxios]);

  useEffect(() => { load(); }, [load]);

  const onDragStart = (draftId, fromDate) => (e) => {
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", draftId);
    setDraggingDraft({ draftId, fromDate });
  };
  const onDragEnd = () => {
    setDraggingDraft(null);
    setDragOverDate("");
  };
  const onDragOver = (date) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (date !== dragOverDate) setDragOverDate(date);
  };
  const onDrop = (newDate) => async (e) => {
    e.preventDefault();
    const draftId = e.dataTransfer.getData("text/plain");
    setDragOverDate("");
    setDraggingDraft(null);
    if (!draftId || !newDate) return;
    // No-op if dropped on same day
    if (draggingDraft && draggingDraft.fromDate === newDate) return;
    try {
      await authAxios.post(`${API_URL}/api/admin/doozyad/weekly-plan/reschedule-draft`, {
        draft_id: draftId, new_date: newDate,
      });
      await load();  // refresh — backend is the source of truth
    } catch (e2) {
      alert(e2?.response?.data?.detail || "Could not reschedule draft");
    }
  };

  const skipDay = async (date) => {
    setBusyDate(date);
    try {
      await authAxios.post(`${API_URL}/api/admin/doozyad/weekly-plan/skip-day`, { date });
      setSkippedDates(p => ({ ...p, [date]: true }));
    } catch (e) {
      alert(e?.response?.data?.detail || "Could not skip");
    } finally {
      setBusyDate("");
    }
  };

  const unskipDay = async (date) => {
    setBusyDate(date);
    try {
      await authAxios.post(`${API_URL}/api/admin/doozyad/weekly-plan/unskip-day`, { date });
      setSkippedDates(p => { const c = { ...p }; delete c[date]; return c; });
    } catch (e) {
      alert(e?.response?.data?.detail || "Could not unskip");
    } finally {
      setBusyDate("");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-[#64748B] py-12 justify-center" data-testid="weekly-plan-loading">
        <Loader2 className="animate-spin" size={20} /> Loading weekly plan...
      </div>
    );
  }

  if (err) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-2 text-sm text-red-700" data-testid="weekly-plan-error">
        <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
        <div className="flex-1">{err}</div>
        <button onClick={load} className="text-red-700 underline text-xs">Retry</button>
      </div>
    );
  }

  if (!plan) return null;

  return (
    <div className="space-y-4" data-testid="weekly-plan-view">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-[#1E1B4B] flex items-center gap-2">
            <Calendar size={20} className="text-[#DC283C]" /> Weekly Ad Plan
          </h3>
          <p className="text-xs text-[#64748B] mt-0.5">
            What DoozyAd will publish over the next 7 days · {plan.summary?.total_festivals || 0} festivals · {plan.summary?.total_scheduled_drafts || 0} drafts already scheduled
          </p>
        </div>
        <button
          onClick={load}
          className="flex items-center gap-1.5 text-xs text-[#64748B] hover:text-[#DC283C] transition-colors"
          data-testid="weekly-plan-refresh"
        >
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      {/* Calendar grid — responsive 1 col → 2 col → 7 col */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-7 gap-3">
        {plan.days.map((d) => {
          const isSkipped = !!skippedDates[d.date];
          const isDragTarget = dragOverDate === d.date;
          const dayHi = isDragTarget
            ? "border-emerald-400 bg-emerald-50 ring-2 ring-emerald-300"
            : d.is_today
              ? "border-[#DC283C] bg-pink-50 ring-2 ring-pink-200"
              : d.festivals.length > 0
                ? "border-amber-300 bg-amber-50"
                : "border-gray-200 bg-white";
          return (
            <div
              key={d.date}
              className={`rounded-lg border-2 p-3 transition-all ${dayHi} ${isSkipped ? "opacity-40" : ""}`}
              data-testid={`weekly-plan-day-${d.date}`}
              onDragOver={onDragOver(d.date)}
              onDragLeave={() => dragOverDate === d.date && setDragOverDate("")}
              onDrop={onDrop(d.date)}
            >
              {/* Date header */}
              <div className="flex items-baseline justify-between mb-2 pb-2 border-b border-gray-200">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[#94a3b8] font-semibold">
                    {d.weekday}{d.is_today && " · Today"}{d.is_tomorrow && " · Tomorrow"}
                  </div>
                  <div className="text-sm font-bold text-[#1E1B4B]">
                    {d.date.slice(8, 10)}/{d.date.slice(5, 7)}
                  </div>
                </div>
                {isSkipped ? (
                  <button
                    onClick={() => unskipDay(d.date)}
                    disabled={busyDate === d.date}
                    className="text-[10px] px-2 py-0.5 rounded bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                    data-testid={`weekly-plan-unskip-${d.date}`}
                  >
                    Re-arm
                  </button>
                ) : (
                  <button
                    onClick={() => skipDay(d.date)}
                    disabled={busyDate === d.date}
                    title="Cancel auto-draft for this day"
                    className="text-[#cbd5e1] hover:text-red-500 transition-colors"
                    data-testid={`weekly-plan-skip-${d.date}`}
                  >
                    <X size={14} />
                  </button>
                )}
              </div>

              {/* Festival pill */}
              {d.festivals.length > 0 && (
                <div className="mb-2 px-2 py-1 rounded bg-gradient-to-r from-amber-200 to-orange-200 text-[11px] font-semibold text-amber-900 flex items-center gap-1">
                  <Sparkles size={10} /> {d.festivals.map(f => f.name).join(", ")}
                </div>
              )}

              {/* Predicted theme */}
              {d.predicted_theme && (
                <div className="mb-2">
                  <div className="text-[10px] uppercase tracking-wider text-[#94a3b8] mb-0.5">Theme</div>
                  <div className="text-xs font-semibold text-[#1E1B4B] line-clamp-2">{d.predicted_theme.name}</div>
                </div>
              )}

              {/* Scheduled drafts — drag to a different day to reschedule */}
              {d.scheduled_drafts.length > 0 && (
                <div className="mb-2 space-y-1">
                  <div className="text-[10px] uppercase tracking-wider text-[#94a3b8]">
                    Scheduled ({d.scheduled_drafts.length})
                  </div>
                  {d.scheduled_drafts.slice(0, 3).map((s) => (
                    <div
                      key={s.draft_id}
                      draggable
                      onDragStart={onDragStart(s.draft_id, d.date)}
                      onDragEnd={onDragEnd}
                      title="Drag to a different day to reschedule"
                      data-testid={`weekly-plan-chip-${s.draft_id}`}
                      className={`text-[10px] text-[#1E1B4B] flex items-center gap-1 px-1.5 py-1 rounded bg-white/80 border border-gray-200 cursor-grab active:cursor-grabbing hover:border-[#DC283C] hover:shadow-sm transition-all ${draggingDraft?.draftId === s.draft_id ? "opacity-40" : ""}`}
                    >
                      <Clock size={9} className="flex-shrink-0 text-[#94a3b8]" />
                      <span className="font-semibold flex-shrink-0">{(s.scheduled_for || "").slice(11, 16)}</span>
                      <span className="truncate">{s.theme_name}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Action chips */}
              <div className="space-y-0.5 pt-1.5 border-t border-gray-100">
                {d.actions.map((a) => (
                  <div
                    key={`${d.date}-${a.type}-${a.time}`}
                    className="text-[9px] text-[#94a3b8] flex items-center gap-1"
                    title={a.label}
                  >
                    <Check size={8} className="text-emerald-400" />
                    {a.time} · {a.label.length > 28 ? a.label.slice(0, 28) + "..." : a.label}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <div className="text-[10px] text-[#94a3b8] italic">
        💡 Click <X className="inline" size={10} /> on any day to cancel that day's auto-draft.
        Drag a scheduled draft chip to a different day to reschedule it.
        The 8 AM cron skips vetoed dates and resumes automatically the next day.
      </div>
    </div>
  );
};
